/**
 * CCC — generic collection browser engine.
 *
 * The coffee collection page cloned for standard collections: a warm sidebar +
 * toolbar with client-side filtering. Filters are built from the product cards
 * themselves (data-* attributes emitted by snippets/ccc-collection-card.liquid),
 * so Brand and Colour work with no Search & Discovery setup.
 *
 * Filter groups: Brand (vendor), Colour (variant colours), Stock status
 * (all / in-stock / preorder / out-of-stock), On sale, plus title search + sort.
 *
 * Client-side, so it filters the products rendered on the page (one paginated
 * page, like the coffee page). Collections larger than one page filter within
 * the loaded set.
 */
(() => {
  const SEARCH_DEBOUNCE_MS = 150;
  const SHOW_MORE_LIMIT = 8;
  const STOCK_VALUES = ['all', 'in-stock', 'preorder', 'out-of-stock'];

  // Group messy variant colour values into base colours (Matte Black -> Black,
  // Candy Orange -> Orange). First matching keyword wins; anything unmatched
  // keeps its own label so nothing is silently dropped.
  const COLOUR_GROUPS = [
    { slug: 'black', label: 'Black', keys: ['black', 'carbon', 'onyx', 'graphite', 'charcoal', 'coal', 'jet'] },
    { slug: 'white', label: 'White', keys: ['white', 'chalk', 'ivory', 'snow'] },
    { slug: 'silver', label: 'Silver', keys: ['silver', 'chrome', 'stainless', 'steel', 'aluminium', 'aluminum', 'platinum'] },
    { slug: 'grey', label: 'Grey', keys: ['grey', 'gray', 'fog', 'slate', 'gunmetal', 'ash', 'smoke'] },
    { slug: 'gold', label: 'Gold', keys: ['gold', 'brass', 'champagne'] },
    { slug: 'copper', label: 'Copper', keys: ['copper', 'bronze'] },
    { slug: 'orange', label: 'Orange', keys: ['orange', 'tangerine', 'amber', 'apricot', 'sunset'] },
    { slug: 'yellow', label: 'Yellow', keys: ['yellow', 'mustard'] },
    { slug: 'green', label: 'Green', keys: ['green', 'lichen', 'olive', 'sage', 'mint', 'forest', 'emerald'] },
    { slug: 'blue', label: 'Blue', keys: ['blue', 'navy', 'teal', 'cyan', 'azure', 'cobalt', 'lagoon'] },
    { slug: 'purple', label: 'Purple', keys: ['purple', 'violet', 'lilac', 'lavender'] },
    { slug: 'pink', label: 'Pink', keys: ['pink', 'rose', 'magenta', 'blush'] },
    { slug: 'red', label: 'Red', keys: ['red', 'crimson', 'burgundy', 'maroon', 'ruby'] },
    { slug: 'brown', label: 'Brown', keys: ['brown', 'walnut', 'wood', 'maple', 'oak', 'sesame', 'woodland', 'tan', 'mocha', 'timber', 'cherry', 'coffee', 'hazel', 'chestnut'] },
    { slug: 'clear', label: 'Clear', keys: ['clear', 'frosted', 'transparent', 'translucent'] },
  ];

  function normalizeColour(rawLabel) {
    const lower = rawLabel.toLowerCase();
    for (const group of COLOUR_GROUPS) {
      if (group.keys.some((key) => lower.includes(key))) return { slug: group.slug, label: group.label };
    }
    const slug = lower.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return { slug: slug || 'other', label: rawLabel };
  }

  function initCollection(root) {
    const grid = root.querySelector('[data-ccc-grid]');
    if (!grid) return;

    const state = {
      brand: new Set(),
      colour: new Set(),
      tag: new Set(),
      stock: 'all',
      sale: false,
      search: '',
      sort: root.querySelector('[data-ccc-sort]')?.value || '',
    };

    let searchDebounceId = 0;
    let lastSort = null;

    const getCards = () => Array.from(grid.querySelectorAll('[data-ccc-card]'));
    const cardColours = (card) => card.cccColours || [];
    const cardTags = (card) => (card.dataset.tags || '').split('|').filter(Boolean);

    // Cache each card's deduped base colours (from normalised variant colours).
    function indexCards(cards) {
      cards.forEach((card) => {
        const set = new Set();
        (card.dataset.colourEntries || '').split('|').filter(Boolean).forEach((entry) => {
          const idx = entry.indexOf(':');
          const rawLabel = idx >= 0 ? entry.slice(idx + 1) : entry;
          if (rawLabel) set.add(normalizeColour(rawLabel).slug);
        });
        card.cccColours = Array.from(set);
      });
    }

    /* ---- matching -------------------------------------------------------- */

    function matchesGroup(card, group, test) {
      switch (group) {
        case 'brand':
          return test.brand.size === 0 || test.brand.has(card.dataset.vendor);
        case 'tag':
          return test.tag.size === 0 || cardTags(card).some((t) => test.tag.has(t));
        case 'colour':
          if (test.colour.size === 0) return true;
          return cardColours(card).some((c) => test.colour.has(c));
        case 'stock':
          return test.stock === 'all' || card.dataset.stockStatus === test.stock;
        case 'sale':
          return !test.sale || card.dataset.onSale === 'true';
        case 'search': {
          const q = test.search.trim().toLowerCase();
          return !q || (card.dataset.productTitle || '').toLowerCase().includes(q);
        }
        default:
          return true;
      }
    }

    const GROUPS = ['tag', 'brand', 'colour', 'stock', 'sale', 'search'];

    function cardMatches(card, test, exceptGroup) {
      return GROUPS.every((g) => g === exceptGroup || matchesGroup(card, g, test));
    }

    /* ---- dynamic Brand + Colour lists ------------------------------------ */

    function buildDynamicFilters(cards) {
      const brands = new Map();
      const colours = new Map();
      const tags = new Map();

      // Brand identifiers, to keep brand names out of the Tags list (the Brand
      // filter already covers them).
      const brandSlugs = new Set();
      const brandWords = new Set();
      cards.forEach((card) => {
        if (card.dataset.vendor) brandSlugs.add(card.dataset.vendor);
        const word = (card.dataset.vendorLabel || '').trim().toLowerCase().split(/\s+/)[0];
        if (word.length >= 4) brandWords.add(word);
      });

      cards.forEach((card) => {
        const bSlug = card.dataset.vendor;
        if (bSlug) {
          if (!brands.has(bSlug)) brands.set(bSlug, { label: card.dataset.vendorLabel || bSlug, count: 0 });
          brands.get(bSlug).count += 1;
        }
        const seenColours = new Set();
        (card.dataset.colourEntries || '').split('|').filter(Boolean).forEach((entry) => {
          const idx = entry.indexOf(':');
          const rawLabel = idx >= 0 ? entry.slice(idx + 1) : entry;
          if (!rawLabel) return;
          const { slug, label } = normalizeColour(rawLabel);
          if (seenColours.has(slug)) return; // count each base colour once per product
          seenColours.add(slug);
          if (!colours.has(slug)) colours.set(slug, { label, count: 0 });
          colours.get(slug).count += 1;
        });

        (card.dataset.tagEntries || '').split('|').filter(Boolean).forEach((entry) => {
          const idx = entry.indexOf(':');
          const slug = idx >= 0 ? entry.slice(0, idx) : entry;
          const label = idx >= 0 ? entry.slice(idx + 1) : entry;
          if (!slug || brandSlugs.has(slug)) return;
          const labelLower = label.toLowerCase();
          if ([...brandWords].some((word) => labelLower.includes(word))) return;
          if (!tags.has(slug)) tags.set(slug, { label, count: 0 });
          tags.get(slug).count += 1;
        });
      });

      renderOptions(root.querySelector('[data-ccc-tag-list]'), 'tag', tags);
      renderOptions(root.querySelector('[data-ccc-brand-list]'), 'brand', brands);
      renderOptions(root.querySelector('[data-ccc-colour-list]'), 'colour', colours);
    }

    function renderOptions(list, group, map) {
      if (!list) return;
      const groupEl = list.closest('[data-ccc-group]');
      const entries = Array.from(map.entries());
      if (group === 'brand' || group === 'tag') {
        // Brands + tags read alphabetically; other lists (colour) stay by count desc.
        entries.sort((a, b) => a[1].label.localeCompare(b[1].label, undefined, { sensitivity: 'base' }));
      } else {
        entries.sort((a, b) => b[1].count - a[1].count || a[1].label.localeCompare(b[1].label));
      }
      if (entries.length === 0) {
        groupEl?.setAttribute('hidden', '');
        return;
      }
      groupEl?.removeAttribute('hidden');

      const frag = document.createDocumentFragment();
      entries.forEach(([slug, { label }]) => {
        const li = document.createElement('li');
        li.className = 'ccc-filters__option';
        const lab = document.createElement('label');
        lab.className = 'ccc-filters__label';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'ccc-filters__input';
        input.setAttribute('data-ccc-filter-input', '');
        input.dataset.group = group;
        input.dataset.value = slug;
        const text = document.createElement('span');
        text.className = 'ccc-filters__text';
        text.textContent = label;
        const count = document.createElement('span');
        count.className = 'ccc-filters__count';
        count.setAttribute('data-ccc-count', '');
        lab.append(input, text, count);
        li.append(lab);
        frag.append(li);
      });
      list.replaceChildren(frag);
      applyShowMore(list, entries.length);
    }

    // Collapse long lists to SHOW_MORE_LIMIT rows behind a "Show all" toggle.
    function applyShowMore(list, total) {
      let toggle = list.nextElementSibling;
      if (!toggle || !toggle.matches?.('[data-ccc-showmore]')) toggle = null;

      if (total > SHOW_MORE_LIMIT) {
        list.classList.add('ccc-filters__list--collapsible');
        Array.from(list.children).forEach((li, index) =>
          li.classList.toggle('ccc-filters__option--overflow', index >= SHOW_MORE_LIMIT)
        );
        if (!toggle) {
          toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'ccc-filters__showmore';
          toggle.setAttribute('data-ccc-showmore', '');
          toggle.addEventListener('click', () => {
            const expanded = list.classList.toggle('is-expanded');
            setToggleLabel(toggle, total, expanded);
          });
          list.after(toggle);
        }
        setToggleLabel(toggle, total, list.classList.contains('is-expanded'));
        toggle.hidden = false;
      } else {
        list.classList.remove('ccc-filters__list--collapsible', 'is-expanded');
        if (toggle) toggle.hidden = true;
      }
    }

    function setToggleLabel(toggle, total, expanded) {
      const more = (root.dataset.labelShowMore || 'Show all ({count})').replace('{count}', String(total));
      toggle.textContent = expanded ? root.dataset.labelShowLess || 'Show less' : more;
    }

    /* ---- apply ----------------------------------------------------------- */

    function applyFilters() {
      const cards = getCards();
      let visible = 0;
      cards.forEach((card) => {
        const show = cardMatches(card, state, null);
        card.classList.toggle('ccc-hidden', !show);
        if (show) visible += 1;
      });
      updateVisibleCount(visible);
      updateFilterCounts(cards);
      if (state.sort !== lastSort) {
        sortCards(cards);
        lastSort = state.sort;
      }
      updateActiveCount();
      const empty = root.querySelector('[data-ccc-empty]');
      if (empty) empty.hidden = visible !== 0;
    }

    function updateVisibleCount(visible) {
      root.querySelectorAll('[data-ccc-visible-count]').forEach((el) => {
        const one = el.dataset.labelOne || '{count} product';
        const many = el.dataset.labelMany || '{count} products';
        el.textContent = (visible === 1 ? one : many).replace('{count}', String(visible));
      });
    }

    function optionCount(cards, group, value) {
      return cards.filter((card) => {
        if (!cardMatches(card, state, group)) return false;
        switch (group) {
          case 'brand':
            return card.dataset.vendor === value;
          case 'tag':
            return cardTags(card).includes(value);
          case 'colour':
            return cardColours(card).includes(value);
          case 'stock':
            return value === 'all' ? true : card.dataset.stockStatus === value;
          case 'sale':
            return card.dataset.onSale === 'true';
          default:
            return true;
        }
      }).length;
    }

    function updateFilterCounts(cards) {
      root.querySelectorAll('[data-ccc-filter-input]').forEach((input) => {
        const { group, value } = input.dataset;
        const count = optionCount(cards, group, value);
        const option = input.closest('.ccc-filters__option') || input.closest('li');
        const countEl = option?.querySelector('[data-ccc-count]');
        if (countEl) countEl.textContent = String(count);
        // Mute zero-count options unless they are the active selection.
        if (option) {
          const mute = count === 0 && !input.checked;
          option.classList.toggle('ccc-filters__option--muted', mute);
        }
      });
    }

    function updateActiveCount() {
      let active = state.brand.size + state.colour.size + state.tag.size;
      if (state.stock !== 'all') active += 1;
      if (state.sale) active += 1;
      if (state.search.trim()) active += 1;
      root.querySelectorAll('[data-ccc-filter-count]').forEach((el) => {
        el.textContent = String(active);
        el.hidden = active === 0;
      });
      root.querySelectorAll('[data-ccc-clear]').forEach((el) => {
        el.hidden = active === 0;
      });
    }

    /* ---- sort ------------------------------------------------------------ */

    function sortCards(cards) {
      const sort = state.sort;
      const num = (card, key) => Number(card.dataset[key]) || 0;
      const compare = (a, b) => {
        switch (sort) {
          case 'price-ascending':
            return num(a, 'variantPrice') - num(b, 'variantPrice');
          case 'price-descending':
            return num(b, 'variantPrice') - num(a, 'variantPrice');
          case 'title-ascending':
            return (a.dataset.productTitle || '').localeCompare(b.dataset.productTitle || '');
          case 'title-descending':
            return (b.dataset.productTitle || '').localeCompare(a.dataset.productTitle || '');
          case 'created-ascending':
            return num(a, 'createdAt') - num(b, 'createdAt');
          case 'created-descending':
            return num(b, 'createdAt') - num(a, 'createdAt');
          default:
            return num(a, 'sortIndex') - num(b, 'sortIndex');
        }
      };
      [...cards].sort(compare).forEach((card) => grid.appendChild(card));
    }

    /* ---- state <-> inputs / URL ------------------------------------------ */

    function readState(input) {
      const { group, value } = input.dataset;
      if (group === 'brand' || group === 'colour' || group === 'tag') {
        if (input.checked) state[group].add(value);
        else state[group].delete(value);
      } else if (group === 'stock') {
        state.stock = STOCK_VALUES.includes(value) ? value : 'all';
      } else if (group === 'sale') {
        state.sale = input.checked;
      }
    }

    function syncInputsFromState() {
      root.querySelectorAll('[data-ccc-filter-input]').forEach((input) => {
        const { group, value } = input.dataset;
        if (group === 'brand' || group === 'colour' || group === 'tag') input.checked = state[group].has(value);
        else if (group === 'stock') input.checked = state.stock === value;
        else if (group === 'sale') input.checked = state.sale;
      });
      root.querySelectorAll('[data-ccc-search]').forEach((el) => {
        if (el.value !== state.search) el.value = state.search;
      });
      const sortSelect = root.querySelector('[data-ccc-sort]');
      if (sortSelect && sortSelect.value !== state.sort) sortSelect.value = state.sort;
    }

    function writeStateToUrl() {
      const params = new URLSearchParams(window.location.search);
      const setOrDelete = (key, val) => (val ? params.set(key, val) : params.delete(key));
      setOrDelete('brand', [...state.brand].join(','));
      setOrDelete('colour', [...state.colour].join(','));
      setOrDelete('tag', [...state.tag].join(','));
      setOrDelete('stock', state.stock === 'all' ? '' : state.stock);
      setOrDelete('sale', state.sale ? '1' : '');
      setOrDelete('search', state.search.trim());
      setOrDelete('sort', state.sort);
      const qs = params.toString();
      window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    }

    function readStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      (params.get('brand') || '').split(',').filter(Boolean).forEach((v) => state.brand.add(v));
      (params.get('colour') || '').split(',').filter(Boolean).forEach((v) => state.colour.add(v));
      (params.get('tag') || '').split(',').filter(Boolean).forEach((v) => state.tag.add(v));
      const stock = params.get('stock');
      if (stock && STOCK_VALUES.includes(stock)) state.stock = stock;
      state.sale = params.get('sale') === '1';
      state.search = params.get('search') || '';
      const sort = params.get('sort');
      if (sort) state.sort = sort;
    }

    function clearAll() {
      state.brand.clear();
      state.colour.clear();
      state.tag.clear();
      state.stock = 'all';
      state.sale = false;
      state.search = '';
      syncInputsFromState();
      applyFilters();
      writeStateToUrl();
    }

    /* ---- events ---------------------------------------------------------- */

    root.addEventListener('change', (event) => {
      const input = event.target.closest?.('[data-ccc-filter-input]');
      if (!input) return;
      readState(input);
      syncInputsFromState();
      applyFilters();
      writeStateToUrl();
    });

    root.querySelectorAll('[data-ccc-search]').forEach((search) => {
      search.addEventListener('input', () => {
        state.search = search.value;
        window.clearTimeout(searchDebounceId);
        searchDebounceId = window.setTimeout(() => {
          syncInputsFromState();
          applyFilters();
          writeStateToUrl();
        }, SEARCH_DEBOUNCE_MS);
      });
      search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
        if (event.key === 'Escape' && search.value) {
          event.preventDefault();
          state.search = '';
          window.clearTimeout(searchDebounceId);
          syncInputsFromState();
          applyFilters();
          writeStateToUrl();
        }
      });
    });

    const sortSelect = root.querySelector('[data-ccc-sort]');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        state.sort = sortSelect.value;
        applyFilters();
        writeStateToUrl();
      });
    }

    root.querySelectorAll('[data-ccc-clear]').forEach((btn) => btn.addEventListener('click', clearAll));

    // Mobile drawer
    const drawer = root.querySelector('[data-ccc-drawer]');
    const openDrawer = () => {
      drawer?.classList.add('is-open');
      drawer?.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ccc-drawer-open');
    };
    const closeDrawer = () => {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ccc-drawer-open');
    };
    root.querySelectorAll('[data-ccc-drawer-open]').forEach((b) => b.addEventListener('click', openDrawer));
    root.querySelectorAll('[data-ccc-drawer-close]').forEach((b) => b.addEventListener('click', closeDrawer));
    drawer?.addEventListener('click', (event) => {
      if (event.target === drawer) closeDrawer();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer?.classList.contains('is-open')) closeDrawer();
    });

    /* ---- load more (collections larger than one page) -------------------- */

    async function loadMore(button) {
      if (button.dataset.loading || !button.dataset.nextUrl) return;
      button.dataset.loading = '1';
      button.disabled = true;
      const restLabel = button.textContent;
      if (button.dataset.loadingLabel) button.textContent = button.dataset.loadingLabel;
      const url = button.dataset.nextUrl;
      const separator = url.includes('?') ? '&' : '?';
      try {
        const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(button.dataset.sectionId)}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fragment = document.createDocumentFragment();
        doc.querySelectorAll('[data-ccc-grid] [data-ccc-card]').forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);

        const nextButton = doc.querySelector('[data-ccc-load-more]');
        if (nextButton && nextButton.dataset.nextUrl) {
          button.dataset.nextUrl = nextButton.dataset.nextUrl;
          button.disabled = false;
          button.textContent = restLabel;
        } else {
          button.closest('.ccc-collection__load-more-wrap')?.remove();
        }

        const cards = getCards();
        indexCards(cards);
        buildDynamicFilters(cards);
        syncInputsFromState();
        lastSort = null;
        applyFilters();
      } catch (error) {
        button.disabled = false;
        button.textContent = restLabel;
      } finally {
        delete button.dataset.loading;
      }
    }

    root.querySelectorAll('[data-ccc-load-more]').forEach((button) =>
      button.addEventListener('click', () => loadMore(button))
    );

    /* ---- init ------------------------------------------------------------ */

    indexCards(getCards());
    buildDynamicFilters(getCards());
    readStateFromUrl();
    syncInputsFromState();
    applyFilters();
  }

  function init() {
    document.querySelectorAll('[data-ccc-collection]').forEach(initCollection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
